import { useCallback, useEffect, useMemo } from 'react'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import { useIntl } from 'react-intl'
import _isEmpty from 'lodash/isEmpty'

import Breadcrumbs from 'components/Breadcrumbs'
import {
  StyledInnerContainerNearMenu,
  StyledOuterContainer,
} from 'components/Layout/CommonLayout/styles'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useJobRouteParams } from 'features/jobs/hooks'
import { JOB_INFO_ROUTES } from 'features/jobs/routes'
import Aside from 'features/aside'
import { fetchJobInfoActions, fetchJobInfoThunk } from 'features/jobs/slice'
import { jobInfoDataSelector, jobInfoSelector } from 'features/jobs/selectors'
import { SpinnerCentered } from 'components/Loader'
import { commonMessages } from 'app/i18n/messages'
import { JobBlockContainer, StyledJobHeader } from 'features/jobs/styles'
import { JobPageContext } from './JobPageContext'
import { jobPageMessages } from 'features/jobs/messages'
import { fetchHomeJobThunk } from 'features/home/components/HomeJobInfo/slice'

function JobPage() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { jobId } = useJobRouteParams()
  const { path, url } = useRouteMatch()
  const isFetching = useAppSelector(jobInfoDataSelector.isFetching)
  const isFinished = useAppSelector(jobInfoDataSelector.isFinished)
  const isError = useAppSelector(jobInfoDataSelector.error)
  const jobInfo = useAppSelector(jobInfoSelector)
  const hasJobInfo = useMemo(() => _isEmpty(jobInfo), [jobInfo])

  const buildJobMenuLinkPath = useCallback(
    (path: string) => `${url}/${path}`,
    [url]
  )
  const buildJobRoutePath = useCallback(
    (routePath: string) => `${path}/${routePath}`,
    [path]
  )

  const defaultRoute = buildJobMenuLinkPath(JOB_INFO_ROUTES[0].path)

  const jobRoutes = useMemo(
    () =>
      JOB_INFO_ROUTES.map(({ component, path }) => ({
        component,
        path: buildJobRoutePath(path),
      })),
    [buildJobRoutePath]
  )

  const jobMenu = useMemo(
    () =>
      JOB_INFO_ROUTES.map(({ title, path, icon }) => ({
        icon,
        title,
        to: buildJobMenuLinkPath(path),
      })),
    [buildJobMenuLinkPath]
  )

  useEffect(() => {
    if (jobId) {
      dispatch(fetchJobInfoThunk(jobId))
      // get job parents to build job path for breadcrumbs and Job Config Files block data
      dispatch(fetchHomeJobThunk(jobId, { configs: true }))
    }

    return () => {
      dispatch(fetchJobInfoActions.clear())
    }
  }, [dispatch, jobId])

  return (
    <StyledOuterContainer>
      <Aside menu={jobMenu} />

      <StyledInnerContainerNearMenu>
        {isFetching && !isFinished && (
          <StyledJobHeader>
            <SpinnerCentered />
          </StyledJobHeader>
        )}
        {isError && (
          <StyledJobHeader>
            {formatMessage(commonMessages.commonError)}
          </StyledJobHeader>
        )}
        {isFinished && (
          <>
            {hasJobInfo ? (
              <JobBlockContainer>
                {formatMessage(jobPageMessages.noJobFound)}
              </JobBlockContainer>
            ) : (
              <JobPageContext.Provider
                value={{
                  jobName: jobInfo.name,
                  jobId,
                  hasHomes: !_isEmpty(jobInfo.homes),
                }}
              >
                <Breadcrumbs />
                <Switch>
                  {jobRoutes.map(({ component, path }) => (
                    <Route key={path} path={path} component={component} />
                  ))}
                  <Route path="*">
                    <Redirect to={defaultRoute} />
                  </Route>
                </Switch>
              </JobPageContext.Provider>
            )}
          </>
        )}
      </StyledInnerContainerNearMenu>
    </StyledOuterContainer>
  )
}

export default JobPage
